import { fieldEntryHasPresentableValue } from '@/common/utils/changedDataFieldMerge'
import {
  collectLayoutFieldNames,
  isLayoutScopedFieldKey,
} from '@/common/utils/layoutScopedClientFields'

export function getTableRowCount(fieldVal: unknown): number {
  if (!fieldVal) return 0
  if (
    typeof fieldVal === 'object' &&
    !Array.isArray(fieldVal) &&
    (fieldVal as { type?: string }).type === 'Array' &&
    Array.isArray((fieldVal as { value?: unknown }).value)
  ) {
    return (fieldVal as { value: unknown[] }).value.length
  }
  const rows =
    (fieldVal as { rows?: unknown[] }).rows ??
    (fieldVal as { value?: { rows?: unknown[] } }).value?.rows
  if (Array.isArray(rows)) return rows.length
  if (Array.isArray(fieldVal)) return fieldVal.length
  return 0
}

/** Сервер явно вернул пустую таблицу (reload/search/filter с 0 строк). */
function isExplicitEmptyTableField(fieldVal: unknown): boolean {
  if (!fieldVal || typeof fieldVal !== 'object' || Array.isArray(fieldVal)) return false
  const entry = fieldVal as { type?: string; value?: unknown }
  if (String(entry.type ?? '') !== 'UniDataTable') return false
  const val = entry.value
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false
  return (val as { empty?: unknown }).empty === true
}

/**
 * TODO: защита от обнуления. Работает, возможно, некорректно, добавлен фикс isExplicitEmptyTableField
 * Вероятно, логика должна быть такая: если элемента нет - не трогаем, если есть - в любом случае заменяем,
 * даже если он пустой. Проверить - есть ли где0то кейсы, когда данные приходят некорректно пустые!
 *
 * @param oldFields
 * @param newFields
 */
export function mergeFieldsDefensive(
  oldFields: Record<string, unknown>,
  newFields: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...oldFields, ...newFields }
  for (const key of Object.keys(newFields)) {
    const oldVal = oldFields[key]
    const newVal = newFields[key]
    // Reload нормализует empty:true|false — всегда авторитетная замена таблицы.
    if (isAuthoritativeUniDataTable(newVal)) {
      merged[key] = newVal
      continue
    }
    const oldCount = getTableRowCount(oldVal)
    const newCount = getTableRowCount(newVal)
    if (oldCount > 0 && newCount === 0) {
      // Авторитетный empty:true (executeScript/reloadElement с is_refill) должен очищать таблицу.
      if (!isExplicitEmptyTableField(newVal)) {
        merged[key] = oldVal
      }
    }
  }
  return merged
}

/** UniDataTable с явным boolean empty (после normalizeReloadTableEmptyFlag). */
export function isAuthoritativeUniDataTable(fieldVal: unknown): boolean {
  if (!fieldVal || typeof fieldVal !== 'object' || Array.isArray(fieldVal)) return false
  const entry = fieldVal as { type?: string; value?: unknown }
  if (String(entry.type ?? '') !== 'UniDataTable') return false
  const val = entry.value
  if (!val || typeof val !== 'object' || Array.isArray(val)) return false
  return typeof (val as { empty?: unknown }).empty === 'boolean'
}

/** New platform session in the same slot — incoming snapshot replaces prior session fields. */
export function isDistinctPlatformSessionChange(
  oldSessionId: unknown,
  newSessionId: unknown,
  options?: { isAdjacentWindow?: boolean },
): boolean {
  if (options?.isAdjacentWindow) return false
  const oldSid = oldSessionId != null ? String(oldSessionId).trim() : ''
  const newSid = newSessionId != null ? String(newSessionId).trim() : ''
  return Boolean(oldSid && newSid && oldSid !== newSid)
}

export function mergePageDataFieldsForSetPageData(
  oldFields: Record<string, unknown>,
  newFields: Record<string, unknown>,
  sessionChanged: boolean,
): Record<string, unknown> {
  if (sessionChanged) {
    return mergeFieldsDefensive({}, newFields)
  }
  return mergeFieldsDefensive(oldFields, newFields)
}

/**
 * Same slot, new form: do not keep the previous form's field keys.
 * Session and $bound often stay put on SAME_WINDOW call_chain_change_doc
 * (create question 586 → view 2582). A later putFormData would resend
 * MESSAGE_TEXT / attachments into explicit_view 586.
 * Schema ids must be explicit payload ids, not the slot id.
 */
export function pageSlotFieldsShouldReplace(options: {
  sessionChanged: boolean
  documentIdentityChanged: boolean
  oldSchemaId: number
  newSchemaId: number
}): boolean {
  if (options.sessionChanged || options.documentIdentityChanged) return true
  return (
    options.oldSchemaId > 0 &&
    options.newSchemaId > 0 &&
    options.oldSchemaId !== options.newSchemaId
  )
}

function typedIdentityNumber(entry: unknown): number | null {
  if (!fieldEntryHasPresentableValue(entry)) return null
  const raw =
    entry && typeof entry === 'object' && 'value' in (entry as object)
      ? (entry as { value: unknown }).value
      : entry
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function typedIdentityString(entry: unknown): string {
  if (!fieldEntryHasPresentableValue(entry)) return ''
  const raw =
    entry && typeof entry === 'object' && 'value' in (entry as object)
      ? (entry as { value: unknown }).value
      : entry
  return raw == null ? '' : String(raw).trim()
}

/**
 * SAME_WINDOW schema replace drops keys the new payload does not repeat.
 * Wizard steps stay on the same service: doAction reloads that document by
 * `_id` / `_typeId`. Keep the previous identity when the next view omits it.
 * A different positive id in the new payload is a real document switch.
 */
export function preserveDocumentIdentityIfIncomingOmitsIt(
  oldFields: Record<string, unknown>,
  newFields: Record<string, unknown>,
): Record<string, unknown> {
  const oldId = typedIdentityNumber(oldFields._id)
  const oldType = typedIdentityString(oldFields._typeId)
  if (oldId == null || oldId <= 0 || !oldType) return newFields

  const newId = typedIdentityNumber(newFields._id)
  if (newId != null && newId > 0 && newId !== oldId) return newFields

  const next = { ...newFields }
  if (newId == null || newId <= 0) next._id = oldFields._id
  if (!typedIdentityString(next._typeId)) next._typeId = oldFields._typeId
  if (
    !fieldEntryHasPresentableValue(next._versionId) &&
    fieldEntryHasPresentableValue(oldFields._versionId)
  ) {
    next._versionId = oldFields._versionId
  }
  return next
}

/** true, если число строк любой UniDataTable отличается */
export function pageDataTableRowCountsDiffer(
  oldData: Record<string, any> | undefined | null,
  newData: Record<string, any> | undefined | null,
): boolean {
  const layers: Array<[Record<string, unknown>, Record<string, unknown>]> = [
    [
      (oldData?.uniData?.fields?.fields as Record<string, unknown>) || {},
      (newData?.uniData?.fields?.fields as Record<string, unknown>) || {},
    ],
    [
      (oldData?.jsResult?.changed_data?.fields?.fields as Record<string, unknown>) || {},
      (newData?.jsResult?.changed_data?.fields?.fields as Record<string, unknown>) || {},
    ],
  ]
  for (const [oldFields, newFields] of layers) {
    const keys = new Set([...Object.keys(oldFields), ...Object.keys(newFields)])
    for (const key of keys) {
      if (getTableRowCount(oldFields[key]) !== getTableRowCount(newFields[key])) {
        return true
      }
    }
  }
  return false
}

/**
 * SAME_WINDOW schema replace keeps whatever the new payload already contains.
 * Create form 586 echoes MESSAGE_TEXT / attachments into view 2582; those names
 * are widgets of the previous layout, not context scalars of the new one.
 * Drop a key only when it belongs to the old layout and not the new one.
 * Non-layout scalars (parentContextId, isExecutor) stay.
 */
export function omitPreviousLayoutFields(
  fields: Record<string, unknown>,
  oldForm: unknown,
  newForm: unknown,
): Record<string, unknown> {
  const oldNames = collectLayoutFieldNames(oldForm)
  const newNames = collectLayoutFieldNames(newForm)
  if (oldNames.size === 0 || newNames.size === 0) return fields

  let dropped = false
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (isLayoutScopedFieldKey(key, oldNames) && !isLayoutScopedFieldKey(key, newNames)) {
      dropped = true
      continue
    }
    next[key] = value
  }
  return dropped ? next : fields
}
